import { FC, useMemo } from 'react';
import { max, sum } from 'd3';

import {
  Accessor,
  BarStacks,
  CategoryAxis,
  categoryScale,
  InteractionItem,
  LinearAxis,
  linearScale,
  Plot,
  TooltipCallbackArgs,
} from '@dbt-labs/sourdough';

import { RunStatus } from '@/__generated__/graphql';
import { useModelExecutionHistoryStatus } from '@/hooks/discovery/modelExecutionHistoryStatus';
import { JobFilter } from '@/pages/account/project/resource/details/components/Tabs/PerformanceView';
import { getISODateUTCArrayFromDateStrings } from '@/util/date';
import { ChartWrapper } from './ChartWrapper';

export interface ModelExecutionStatusStackedBarChartProps {
  environmentId: number;
  startDate: string;
  endDate: string;
  uniqueId: string;
  setOldestDate: (date: Date | undefined) => void;
  onItemClicked?: (date: JobFilter) => void;
}

type GroupedData = {
  date: string;
  values: [number, number, number, number]; // [Error, Reused, Skipped, Success]
};

const FilterButton = ({
  children,
  isDisabled,
  onClick,
}: {
  children: string;
  isDisabled?: boolean;
  onClick: () => void;
}) => {
  if (isDisabled) {
    return <div>{children}</div>;
  }
  return (
    <button className="block text-fgBrand" onClick={onClick}>
      {children}
    </button>
  );
};

export const ModelExecutionStatusStackedBarChart: FC<
  ModelExecutionStatusStackedBarChartProps
> = ({
  environmentId,
  startDate,
  endDate,
  uniqueId,
  setOldestDate,
  onItemClicked = () => {},
}) => {
  const [executionHistory] = useModelExecutionHistoryStatus({
    environmentId: environmentId,
    startDate: startDate,
    endDate: endDate,
    uniqueId: uniqueId,
  });
  setOldestDate(undefined);
  const groupedData = useMemo((): GroupedData[] | undefined => {
    const modelExecutionHistory =
      executionHistory.data?.performance.modelExecutionHistory;
    if (!modelExecutionHistory) return undefined;
    const dateArray = getISODateUTCArrayFromDateStrings(startDate, endDate);

    const dayMap = modelExecutionHistory.reduce<Record<string, any>>((acc, curr) => {
      const getData = ({ date, executionsByJob }: typeof curr) => {
        const statusCounts: Record<string, number> = {};
        Object.values(RunStatus).forEach((status) => {
          statusCounts[status] = 0;
        });

        executionsByJob?.forEach((execution) => {
          execution.executions?.forEach(({ status }) => {
            if (status && statusCounts.hasOwnProperty(status)) {
              statusCounts[status]++;
            }
          });
        });

        // Return counts in consistent order: Error, Reused, Skipped, Success
        const orderedStatuses = [
          RunStatus.Error,
          RunStatus.Reused,
          RunStatus.Skipped,
          RunStatus.Success,
        ];
        const values = orderedStatuses.map((status) => statusCounts[status]);

        return { date, values };
      };
      acc[curr.date] = getData(curr);
      return acc;
    }, {});

    const chartData = dateArray.map((date) => {
      const data = dayMap?.[date];
      if (data) {
        return data;
      }
      return {
        date,
        values: [0, 0, 0, 0], // [Error, Reused, Skipped, Success]
      };
    });
    if (chartData?.every((x) => x.values.every((y: number) => y === 0))) {
      return [];
    }
    return chartData;
  }, [endDate, executionHistory.data?.performance.modelExecutionHistory, startDate]);

  const xAccessor: Accessor<GroupedData, string> = (x: GroupedData) => {
    const [, MM, DD] = x.date.split('-');
    return `${MM}-${DD}`;
  };
  const yAccessor = (d: GroupedData) => sum(d.values);
  const xScale = categoryScale({
    labels: groupedData?.map(xAccessor) ?? [],
  });

  const yScale = linearScale({
    min: 0,
    max: max(groupedData ?? [], yAccessor) ?? 0,
  });

  return (
    <div className="h-[300px]">
      <ChartWrapper
        testId="model-execution-status-stacked-bar-chart"
        numDataPoints={groupedData?.length}
        isLoading={executionHistory.fetching}
        error={executionHistory.error?.message}
        height={300}
        margin={{ bottom: 30, left: 45, right: 5, top: 25 }}
        xAccessor={xAccessor}
        xScale={xScale}
        yAccessor={(d: GroupedData) => sum(d.values)}
        yScale={yScale}
        noDataMessage="No data available"
        tooltip={({ interactionItem }: TooltipCallbackArgs<GroupedData>) => {
          const item = interactionItem?.item;
          if (!item) return null;
          const [errors, reused, skipped, successes] = item.values;

          const statusData = [
            {
              count: errors,
              status: RunStatus.Error,
              label: 'build with errors',
              labelPlural: 'builds with errors',
            },
            {
              count: reused,
              status: RunStatus.Reused,
              label: 'reused build',
              labelPlural: 'reused builds',
            },
            {
              count: skipped,
              status: RunStatus.Skipped,
              label: 'skipped build',
              labelPlural: 'skipped builds',
            },
            {
              count: successes,
              status: RunStatus.Success,
              label: 'successful build',
              labelPlural: 'successful builds',
            },
          ];

          return (
            <div>
              <div>{item.date}</div>
              {statusData.map(({ count, status, label, labelPlural }) => {
                const displayText =
                  count === 1 ? `1 ${label}` : `${count} ${labelPlural}`;
                return (
                  <FilterButton
                    key={status}
                    isDisabled={count === 0}
                    onClick={() => {
                      if (item) {
                        onItemClicked?.({ ...item, status });
                      }
                    }}
                  >
                    {displayText}
                  </FilterButton>
                );
              })}
            </div>
          );
        }}
        onItemClick={({ item }: InteractionItem<GroupedData>) => {
          onItemClicked?.(item);
        }}
        colors={[
          // error
          {
            fill: 'fill-fgVizRed',
            legendIconColor: 'bg-fgVizRed',
            unfocused: 'opacity-20 stroke-opacity-20',
            focused: '',
          },
          // reused
          {
            fill: 'fill-fgVizIndigo',
            legendIconColor: 'bg-fgVizIndigo',
            unfocused: 'opacity-20 stroke-opacity-20',
            focused: '',
          },
          // skipped
          {
            fill: 'fill-fgVizAmber',
            legendIconColor: 'bg-fgVizAmber',
            unfocused: 'opacity-20 stroke-opacity-20',
            focused: '',
          },
          // success
          {
            fill: 'fill-fgVizEmerald',
            legendIconColor: 'bg-fgVizEmerald',
            unfocused: 'opacity-20 stroke-opacity-20',
            focused: '',
          },
        ]}
        legendProps={{
          width: 75,
          position: 'right',
        }}
      >
        <LinearAxis axis="y" label="# of Executions" />
        <CategoryAxis axis="x" label="Execution Date" />
        <Plot
          data={groupedData ?? []}
          itemComponent={BarStacks}
          extraItemProps={{
            groupValuesAccessor: (item: any): number[] => item.values,
          }}
          name={['Failed', 'Reused', 'Skipped', 'Success']}
        />
      </ChartWrapper>
    </div>
  );
};
